import { useState, useEffect } from 'react';
import { 
  Users, DollarSign, TrendingUp, CheckCircle, XCircle, 
  Clock, Eye, Wallet, Gift, Settings, Plus, Edit, Trash2,
  Loader2, IndianRupee, Search, Filter, AlertTriangle, Zap, Award, Star
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import axios from 'axios';
import { API_URL } from '../../lib/api-client';
import { handleApiError } from '../../utils/apiErrorHandler';
import { PaginationWrapper } from '../ui/PaginationWrapper';

export default function ReferralAdminSection() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [earnings, setEarnings] = useState<any[]>([]);
  const [payouts, setPayouts] = useState<any[]>([]);
  const [rewards, setRewards] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>({});
  const [stats, setStats] = useState<any>({});
  const [plans, setPlans] = useState<any[]>([]);
  const [qualifiedUsers, setQualifiedUsers] = useState<any>({ qualifiedRewards: [], month: '', year: 0 });
  const [grantingReward, setGrantingReward] = useState(false);
  
  const [earningsFilter, setEarningsFilter] = useState('all');
  const [payoutsFilter, setPayoutsFilter] = useState('all');
  const [earningsPage, setEarningsPage] = useState(1);
  const [earningsPerPage, setEarningsPerPage] = useState(20);
  const [payoutsPage, setPayoutsPage] = useState(1);
  const [payoutsPerPage, setPayoutsPerPage] = useState(20);
  
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [showRewardDialog, setShowRewardDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  
  const [selectedEarning, setSelectedEarning] = useState<any>(null);
  const [selectedPayout, setSelectedPayout] = useState<any>(null);
  const [selectedReward, setSelectedReward] = useState<any>(null);
  
  const [rejectReason, setRejectReason] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [adminNote, setAdminNote] = useState('');
  
  const [rewardForm, setRewardForm] = useState({
    title: '',
    description: '',
    rewardAmount: 0,
    icon: 'gift',
    color: 'rose',
    isActive: true,
    displayOrder: 0,
    criteriaType: 'referral_count',
    minReferrals: 0,
    minPlansSold: 0,
    timeframe: 'lifetime',
    autoCalculate: false,
    requiresApproval: true
  });
  
  const [settingsForm, setSettingsForm] = useState({
    referralAmount: 100,
    referralType: 'fixed',
    referralPercentage: 10,
    minimumPayoutAmount: 500,
    autoApproveReferrals: false,
    requireOrderForReferral: true,
    termsAndConditions: '',
    welcomeMessage: '',
    earningStructureMessages: [] as Array<{
      title: string;
      description: string;
      amount: number;
    }>,
    planCommissions: [] as Array<{
      planId: string;
      planName: string;
      planPrice: number;
      commissionType: 'fixed' | 'percentage';
      commissionValue: number;
    }>
  });

  useEffect(() => {
    fetchData();
  }, [earningsFilter, payoutsFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please login as admin to access this section');
        setLoading(false);
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const [earningsRes, payoutsRes, rewardsRes, settingsRes, plansRes] = await Promise.all([
        axios.get(`${API_URL}/admin/referrals/earnings?status=${earningsFilter === 'all' ? '' : earningsFilter}`),
        axios.get(`${API_URL}/admin/referrals/payouts?status=${payoutsFilter === 'all' ? '' : payoutsFilter}`),
        axios.get(`${API_URL}/admin/referrals/rewards`),
        axios.get(`${API_URL}/admin/referrals/settings`),
        axios.get(`${API_URL}/plans?admin=true`)
      ]);

      // Real data loaded successfully from backend
      if (earningsRes.data.success) {
        setEarnings(earningsRes.data.data.earnings || []);
        setStats(earningsRes.data.data.stats || {});
      }

      if (payoutsRes.data.success) {
        setPayouts(payoutsRes.data.data.requests || []);
      }

      if (rewardsRes.data.success) {
        setRewards(rewardsRes.data.data.rewards || []);
      }

      if (plansRes.data.success) {
        const loadedPlans = plansRes.data.data.plans || [];
        setPlans(loadedPlans);
      }

      if (settingsRes.data.success) {
        const loadedSettings = settingsRes.data.data.settings;
        setSettings(loadedSettings);
        
        // Initialize plan commissions if not exists
        let planCommissions = loadedSettings.planCommissions || [];
        if (planCommissions.length === 0 && plansRes.data.success) {
          const loadedPlans = plansRes.data.data.plans || [];
          planCommissions = loadedPlans.map((plan: any) => ({
            planId: plan._id,
            planName: plan.name,
            planPrice: plan.finalPrice || plan.price,
            commissionType: 'fixed',
            commissionValue: loadedSettings.referralAmount || 100
          }));
        }
        
        setSettingsForm({
          referralAmount: loadedSettings.referralAmount || 100,
          referralType: loadedSettings.referralType || 'fixed',
          referralPercentage: loadedSettings.referralPercentage || 10,
          minimumPayoutAmount: loadedSettings.minimumPayoutAmount || 500,
          autoApproveReferrals: loadedSettings.autoApproveReferrals || false,
          requireOrderForReferral: loadedSettings.requireOrderForReferral !== false,
          termsAndConditions: loadedSettings.termsAndConditions || '',
          welcomeMessage: loadedSettings.welcomeMessage || 'Refer friends and earn rewards!',
          earningStructureMessages: loadedSettings.earningStructureMessages || [],
          planCommissions
        });
      }

      setError(null);
      setLoading(false);
    } catch (error: any) {
      // Development fallback - show error but provide mock data for UI testing
      // Note: Not calling handleApiError to avoid console errors when using mock data
      
      // Set user-friendly error message
      if (error.code === 'ERR_NETWORK') {
        setError('⚠️ Using development mock data - Backend server is not running. Start with: cd backend && npm start');
      } else if (error.response?.status === 401) {
        setError('Unauthorized access. Please login as admin.');
      } else if (error.response?.status === 404) {
        setError('API endpoint not found. Backend may need to be updated.');
      } else {
        setError('Failed to load referral data. Using mock data for development.');
      }
      
      // Load mock data for development
      setEarnings([
        {
          _id: '1',
          referrerId: { firstName: 'John', lastName: 'Doe', referralCode: 'JOHN123' },
          referredUserId: { firstName: 'Jane', lastName: 'Smith' },
          amount: 100,
          status: 'pending',
          couponCode: 'REFER100',
          createdAt: new Date().toISOString()
        },
        {
          _id: '2',
          referrerId: { firstName: 'Mike', lastName: 'Johnson', referralCode: 'MIKE456' },
          referredUserId: { firstName: 'Sarah', lastName: 'Williams' },
          amount: 100,
          status: 'approved',
          couponCode: 'REFER200',
          createdAt: new Date(Date.now() - 86400000).toISOString()
        }
      ]);
      
      setStats([
        { _id: 'pending', count: 5, totalAmount: 500 },
        { _id: 'approved', count: 15, totalAmount: 1500 },
        { _id: 'rejected', count: 2, totalAmount: 200 }
      ]);
      
      setPayouts([
        {
          _id: '1',
          userId: { firstName: 'John', lastName: 'Doe', email: 'john@example.com', phone: '+1234567890' },
          amount: 1000,
          status: 'pending',
          paymentMethod: {
            type: 'upi',
            details: { upiId: 'john@paytm' }
          },
          createdAt: new Date().toISOString()
        },
        {
          _id: '2',
          userId: { firstName: 'Mike', lastName: 'Johnson', email: 'mike@example.com', phone: '+1234567891' },
          amount: 2000,
          status: 'completed',
          paymentMethod: {
            type: 'bank_transfer',
            details: {
              accountNumber: '1234567890',
              ifscCode: 'BANK0001234',
              accountHolderName: 'Mike Johnson',
              bankName: 'Test Bank'
            }
          },
          paymentReference: 'TXN123456789',
          createdAt: new Date(Date.now() - 172800000).toISOString()
        }
      ]);
      
      setRewards([
        {
          _id: '1',
          title: 'First Referral Bonus',
          description: 'Earn ₹100 for your first successful referral',
          rewardAmount: 100,
          icon: 'gift',
          color: 'rose',
          isActive: true,
          displayOrder: 1
        },
        {
          _id: '2',
          title: '5 Referrals Milestone',
          description: 'Get ₹500 bonus when you refer 5 friends',
          rewardAmount: 500,
          icon: 'star',
          color: 'amber',
          isActive: true,
          displayOrder: 2
        }
      ]);
      
      setSettings({
        referralAmount: 100,
        referralType: 'fixed',
        referralPercentage: 10,
        minimumPayoutAmount: 500,
        autoApproveReferrals: false,
        requireOrderForReferral: true,
        termsAndConditions: 'Mock terms and conditions for development',
        welcomeMessage: 'Refer friends and earn rewards!',
        planCommissions: []
      });
      
      setLoading(false);
    }
  };

  const handleApproveEarning = async () => {
    if (!selectedEarning) return;
    
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.post(`${API_URL}/admin/referrals/earnings/${selectedEarning._id}/approve`);

      if (response.data.success) {
        toast.success('Referral earning approved');
        setShowApproveDialog(false);
        setSelectedEarning(null);
        fetchData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to approve earning');
    }
  };

  const handleRejectEarning = async () => {
    if (!selectedEarning || !rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.post(`${API_URL}/admin/referrals/earnings/${selectedEarning._id}/reject`, {
        reason: rejectReason
      });

      if (response.data.success) {
        toast.success('Referral earning rejected');
        setShowRejectDialog(false);
        setSelectedEarning(null);
        setRejectReason('');
        fetchData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject earning');
    }
  };

  const handleApprovePayout = async () => {
    if (!selectedPayout) return;

    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.post(`${API_URL}/admin/referrals/payouts/${selectedPayout._id}/approve`, {
        paymentReference,
        adminNote
      });

      if (response.data.success) {
        toast.success('Payout approved successfully');
        setShowPayoutDialog(false);
        setSelectedPayout(null);
        setPaymentReference('');
        setAdminNote('');
        fetchData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to approve payout');
    }
  };

  const handleRejectPayout = async () => {
    if (!selectedPayout || !rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.post(`${API_URL}/admin/referrals/payouts/${selectedPayout._id}/reject`, {
        reason: rejectReason
      });

      if (response.data.success) {
        toast.success('Payout rejected');
        setShowPayoutDialog(false);
        setSelectedPayout(null);
        setRejectReason('');
        fetchData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject payout');
    }
  };

  const handleSaveReward = async () => {
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      let response;
      if (selectedReward) {
        response = await axios.put(`${API_URL}/admin/referrals/rewards/${selectedReward._id}`, rewardForm);
      } else {
        response = await axios.post(`${API_URL}/admin/referrals/rewards`, rewardForm);
      }

      if (response.data.success) {
        toast.success(selectedReward ? 'Reward updated' : 'Reward created');
        setShowRewardDialog(false);
        setSelectedReward(null);
        setRewardForm({
          title: '',
          description: '',
          rewardAmount: 0,
          icon: 'gift',
          color: 'rose',
          isActive: true,
          displayOrder: 0
        });
        fetchData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save reward');
    }
  };

  const handleDeleteReward = async (rewardId: string) => {
    if (!confirm('Are you sure you want to delete this reward?')) return;

    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.delete(`${API_URL}/admin/referrals/rewards/${rewardId}`);

      if (response.data.success) {
        toast.success('Reward deleted');
        fetchData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete reward');
    }
  };

  const handleCreateDefaultRewards = async () => {
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.post(`${API_URL}/admin/referrals/rewards/create-defaults`);

      if (response.data.success) {
        toast.success('Default reward templates created successfully');
        fetchData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create default rewards');
    }
  };

  const handleSaveSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.put(`${API_URL}/admin/referrals/settings`, settingsForm);

      if (response.data.success) {
        toast.success('Settings updated successfully');
        setShowSettingsDialog(false);
        fetchData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update settings');
    }
  };

  const fetchQualifiedUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.get(`${API_URL}/admin/referrals/rewards/qualified-users`);

      if (response.data.success) {
        setQualifiedUsers(response.data.data);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch qualified users');
    }
  };

  const handleGrantReward = async (userId: string, rewardId: string, month: string) => {
    try {
      setGrantingReward(true);
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.post(`${API_URL}/admin/referrals/rewards/grant`, {
        userId,
        rewardId,
        month
      });

      if (response.data.success) {
        toast.success('Reward granted successfully! User will be notified.');
        fetchQualifiedUsers();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to grant reward');
    } finally {
      setGrantingReward(false);
    }
  };

  const handleGrantBulkRewards = async (rewardId: string, userIds: string[], month: string) => {
    if (!confirm(`Grant rewards to ${userIds.length} users? They will be notified immediately.`)) return;

    try {
      setGrantingReward(true);
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.post(`${API_URL}/admin/referrals/rewards/grant-bulk`, {
        rewardId,
        userIds,
        month
      });

      if (response.data.success) {
        const { success, failed } = response.data.data;
        toast.success(`Granted ${success.length} rewards successfully${failed.length > 0 ? `, ${failed.length} failed` : ''}`);
        fetchQualifiedUsers();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to grant bulk rewards');
    } finally {
      setGrantingReward(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, any> = {
      pending: { variant: 'secondary', label: 'Pending' },
      approved: { variant: 'default', label: 'Approved' },
      rejected: { variant: 'destructive', label: 'Rejected' },
      completed: { variant: 'default', label: 'Completed' },
      processing: { variant: 'secondary', label: 'Processing' }
    };
    return <Badge variant={config[status]?.variant}>{config[status]?.label || status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error/Development Banner */}
      {error && (
        <Card className={error.includes('mock data') ? 'border-amber-200 bg-amber-50' : 'border-red-200 bg-red-50'}>
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className={`h-5 w-5 mt-0.5 ${error.includes('mock data') ? 'text-amber-600' : 'text-red-600'}`} />
              <div className="flex-1">
                <h3 className={`font-semibold mb-1 ${error.includes('mock data') ? 'text-amber-900' : 'text-red-900'}`}>
                  {error.includes('mock data') ? 'Development Mode' : 'Connection Error'}
                </h3>
                <p className={`text-sm mb-3 ${error.includes('mock data') ? 'text-amber-700' : 'text-red-700'}`}>{error}</p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => fetchData()}
                    className={error.includes('mock data') ? 'border-amber-300 text-amber-700 hover:bg-amber-100' : 'border-red-300 text-red-700 hover:bg-red-100'}
                  >
                    Retry Connection
                  </Button>
                  {error.includes('mock data') && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setError(null)}
                      className="text-amber-700 hover:bg-amber-100"
                    >
                      Dismiss
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {stats && Array.isArray(stats) && stats.length > 0 && stats.map((stat: any) => (
          <Card key={stat._id}>
            <CardHeader className="pb-3">
              <CardDescription>{stat._id}</CardDescription>
              <CardTitle className="text-2xl">
                {stat.count} <span className="text-sm text-gray-500">items</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Total: ₹{stat.totalAmount?.toLocaleString() || 0}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Settings Button */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Referral & Wallet Management</CardTitle>
              <CardDescription>Manage referral earnings, payouts, rewards, and settings</CardDescription>
            </div>
            <Button onClick={() => setShowSettingsDialog(true)}>
              <Settings className="h-4 w-4 mr-2" />
              Configure Settings
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Tabs defaultValue="earnings" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="earnings">Referral Earnings</TabsTrigger>
          <TabsTrigger value="payouts">Payout Requests</TabsTrigger>
          <TabsTrigger value="rewards">Rewards</TabsTrigger>
          <TabsTrigger value="qualified" onClick={() => fetchQualifiedUsers()}>
            Qualified Users
          </TabsTrigger>
        </TabsList>

        {/* Earnings Tab */}
        <TabsContent value="earnings" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Referral Earnings</CardTitle>
                <Select value={earningsFilter} onValueChange={setEarningsFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Earnings</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {earnings.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No earnings found
                </div>
              ) : (
                <>
                <div className="space-y-3">
                  {earnings.slice((earningsPage - 1) * earningsPerPage, earningsPage * earningsPerPage).map((earning: any) => (
                    <div key={earning._id} className="p-4 border rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">
                            {earning.referrerId?.firstName} {earning.referrerId?.lastName}
                          </p>
                          <p className="text-sm text-gray-500">
                            Referrer Code: {earning.referrerId?.referralCode}
                          </p>
                        </div>
                        {getStatusBadge(earning.status)}
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Referred User:</p>
                          <p className="font-medium">
                            {earning.referredUserId?.firstName} {earning.referredUserId?.lastName}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Amount:</p>
                          <p className="font-semibold text-green-600">₹{earning.amount}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Date:</p>
                          <p>{new Date(earning.createdAt).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Coupon Code:</p>
                          <p>{earning.couponCode}</p>
                        </div>
                      </div>
                      {earning.status === 'pending' && (
                        <div className="flex gap-2 pt-2">
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => {
                              setSelectedEarning(earning);
                              setShowApproveDialog(true);
                            }}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              setSelectedEarning(earning);
                              setShowRejectDialog(true);
                            }}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}
                      {earning.status === 'rejected' && earning.rejectionReason && (
                        <div className="p-2 bg-red-50 text-red-700 rounded text-sm">
                          Reason: {earning.rejectionReason}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {earnings.length > 0 && (
                  <div className="mt-4">
                    <PaginationWrapper
                      currentPage={earningsPage}
                      totalPages={Math.ceil(earnings.length / earningsPerPage)}
                      onPageChange={setEarningsPage}
                      totalItems={earnings.length}
                      itemsPerPage={earningsPerPage}
                      onItemsPerPageChange={setEarningsPerPage}
                    />
                  </div>
                )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payouts Tab */}
        <TabsContent value="payouts" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Payout Requests</CardTitle>
                <Select value={payoutsFilter} onValueChange={setPayoutsFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Requests</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {payouts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No payout requests found
                </div>
              ) : (
                <div className="space-y-3">
                  {payouts.map((payout: any) => (
                    <div key={payout._id} className="p-4 border rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">
                            {payout.userId?.firstName} {payout.userId?.lastName}
                          </p>
                          <p className="text-sm text-gray-500">{payout.userId?.email}</p>
                        </div>
                        {getStatusBadge(payout.status)}
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Amount:</p>
                          <p className="font-semibold text-lg">₹{payout.amount}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Method:</p>
                          <p className="capitalize">{payout.paymentMethod.type.replace('_', ' ')}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Requested:</p>
                          <p>{new Date(payout.createdAt).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Phone:</p>
                          <p>{payout.userId?.phone || 'N/A'}</p>
                        </div>
                      </div>
                      
                      {/* Payment Details */}
                      <div className="p-3 bg-gray-50 rounded text-sm space-y-1">
                        <p className="font-medium">Payment Details:</p>
                        {payout.paymentMethod.type === 'upi' && (
                          <p>UPI ID: {payout.paymentMethod.details.upiId}</p>
                        )}
                        {payout.paymentMethod.type === 'bank_transfer' && (
                          <>
                            <p>Acc: {payout.paymentMethod.details.accountNumber}</p>
                            <p>IFSC: {payout.paymentMethod.details.ifscCode}</p>
                            <p>Name: {payout.paymentMethod.details.accountHolderName}</p>
                            <p>Bank: {payout.paymentMethod.details.bankName}</p>
                          </>
                        )}
                      </div>

                      {payout.status === 'pending' && (
                        <div className="flex gap-2 pt-2">
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => {
                              setSelectedPayout(payout);
                              setShowPayoutDialog(true);
                            }}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Approve & Process
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              setSelectedPayout(payout);
                              setShowPayoutDialog(true);
                            }}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}
                      
                      {payout.paymentReference && (
                        <div className="p-2 bg-green-50 text-green-700 rounded text-sm">
                          Payment Ref: {payout.paymentReference}
                        </div>
                      )}
                      {payout.rejectionReason && (
                        <div className="p-2 bg-red-50 text-red-700 rounded text-sm">
                          Reason: {payout.rejectionReason}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rewards Tab */}
        <TabsContent value="rewards" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Referral Rewards</CardTitle>
                <div className="flex gap-2">
                  {rewards.length === 0 && (
                    <Button 
                      variant="outline"
                      onClick={handleCreateDefaultRewards}
                    >
                      <Zap className="h-4 w-4 mr-2" />
                      Create Default Templates
                    </Button>
                  )}
                  <Button onClick={() => {
                    setSelectedReward(null);
                    setRewardForm({
                      title: '',
                      description: '',
                      rewardAmount: 0,
                      icon: 'gift',
                      color: 'rose',
                      isActive: true,
                      displayOrder: 0,
                      criteriaType: 'referral_count',
                      minReferrals: 0,
                      minPlansSold: 0,
                      timeframe: 'lifetime',
                      autoCalculate: false,
                      requiresApproval: true
                    });
                    setShowRewardDialog(true);
                  }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Reward
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {rewards.length === 0 ? (
                <div className="text-center py-8">
                  <Gift className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-2">No rewards configured</p>
                  <p className="text-sm text-gray-400">Create default templates or add custom rewards</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {rewards.map((reward: any) => (
                    <div key={reward._id} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold">{reward.title}</h4>
                            {!reward.isActive && (
                              <Badge variant="secondary">Inactive</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{reward.description}</p>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="font-medium text-green-600">₹{reward.rewardAmount}</span>
                            <span className="text-gray-500">Order: {reward.displayOrder}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedReward(reward);
                              setRewardForm(reward);
                              setShowRewardDialog(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteReward(reward._id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Qualified Users Tab */}
        <TabsContent value="qualified" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Qualified Users - {qualifiedUsers.month}</CardTitle>
                  <CardDescription>Users who have qualified for milestone rewards this month</CardDescription>
                </div>
                <Button onClick={fetchQualifiedUsers} variant="outline">
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {qualifiedUsers.qualifiedRewards?.length === 0 ? (
                <div className="text-center py-12">
                  <Award className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-2">No qualified users this month</p>
                  <p className="text-sm text-gray-400">
                    Users will appear here when they meet the criteria for milestone rewards
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {qualifiedUsers.qualifiedRewards?.map((item: any) => (
                    <div key={item.reward._id} className="border rounded-lg p-4 space-y-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`h-8 w-8 rounded-full bg-${item.reward.color}-100 flex items-center justify-center`}>
                              {item.reward.icon === 'gift' && <Gift className="h-4 w-4 text-rose-600" />}
                              {item.reward.icon === 'star' && <Star className="h-4 w-4 text-green-600" />}
                              {item.reward.icon === 'award' && <Award className="h-4 w-4 text-orange-600" />}
                              {item.reward.icon === 'trending-up' && <TrendingUp className="h-4 w-4 text-blue-600" />}
                              {item.reward.icon === 'zap' && <Zap className="h-4 w-4 text-yellow-600" />}
                              {item.reward.icon === 'crown' && <Award className="h-4 w-4 text-purple-600" />}
                            </div>
                            <div>
                              <h4 className="font-semibold">{item.reward.title}</h4>
                              <p className="text-sm text-gray-600">{item.reward.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-sm mt-2">
                            <Badge variant="secondary">₹{item.reward.rewardAmount}</Badge>
                            <Badge>
                              {item.reward.criteriaType === 'plan_sales' 
                                ? `${item.reward.minPlansSold}+ Plans Sold`
                                : `${item.reward.minReferrals}+ Referrals`}
                            </Badge>
                            <Badge variant="outline">{item.reward.timeframe}</Badge>
                            <span className="text-green-600 font-medium">{item.totalQualified} qualified</span>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleGrantBulkRewards(
                            item.reward._id,
                            item.qualifiedUsers.map((u: any) => u.userId),
                            qualifiedUsers.month
                          )}
                          disabled={grantingReward}
                          size="sm"
                        >
                          {grantingReward ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle className="h-4 w-4 mr-2" />
                          )}
                          Grant to All
                        </Button>
                      </div>

                      {/* Qualified Users List */}
                      <div className="border-t pt-3 space-y-2">
                        {item.qualifiedUsers.map((user: any) => (
                          <div key={user.userId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <p className="font-medium">
                                {user.firstName} {user.lastName}
                              </p>
                              <p className="text-sm text-gray-600">{user.email}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-xs">
                                  {user.referralCode}
                                </Badge>
                                {user.qualificationData.plansSold !== undefined && (
                                  <span className="text-xs text-gray-500">
                                    {user.qualificationData.plansSold} plans sold
                                  </span>
                                )}
                                {user.qualificationData.referralCount !== undefined && (
                                  <span className="text-xs text-gray-500">
                                    {user.qualificationData.referralCount} referrals
                                  </span>
                                )}
                                {user.qualificationData.totalEarnings !== undefined && (
                                  <span className="text-xs text-green-600">
                                    ₹{user.qualificationData.totalEarnings} earned
                                  </span>
                                )}
                              </div>
                            </div>
                            <Button
                              onClick={() => handleGrantReward(
                                user.userId,
                                item.reward._id,
                                qualifiedUsers.month
                              )}
                              disabled={grantingReward}
                              size="sm"
                              variant="outline"
                            >
                              {grantingReward ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Grant
                                </>
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Approve Earning Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Referral Earning</DialogTitle>
            <DialogDescription>
              This will credit ₹{selectedEarning?.amount} to the user's wallet.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleApproveEarning}>
              Approve & Credit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Earning Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Referral Earning</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejection.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Rejection Reason</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowRejectDialog(false);
              setRejectReason('');
            }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRejectEarning}>
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payout Action Dialog */}
      <Dialog open={showPayoutDialog} onOpenChange={setShowPayoutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process Payout Request</DialogTitle>
            <DialogDescription>
              Amount: ₹{selectedPayout?.amount}
            </DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="approve">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="approve">Approve</TabsTrigger>
              <TabsTrigger value="reject">Reject</TabsTrigger>
            </TabsList>
            <TabsContent value="approve" className="space-y-4">
              <div>
                <Label>Payment Reference (UTR/Transaction ID)</Label>
                <Input
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="Enter reference number"
                />
              </div>
              <div>
                <Label>Admin Note (Optional)</Label>
                <Textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="Any notes..."
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowPayoutDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleApprovePayout}>
                  Approve Payout
                </Button>
              </DialogFooter>
            </TabsContent>
            <TabsContent value="reject" className="space-y-4">
              <div>
                <Label>Rejection Reason</Label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter reason..."
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowPayoutDialog(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleRejectPayout}>
                  Reject Payout
                </Button>
              </DialogFooter>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Reward Dialog */}
      <Dialog open={showRewardDialog} onOpenChange={setShowRewardDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedReward ? 'Edit' : 'Add'} Reward</DialogTitle>
            <DialogDescription>
              {!selectedReward && 'Use a template or create a custom reward'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!selectedReward && (
              <div>
                <Label>Quick Templates</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setRewardForm({
                      title: 'First Referral Bonus',
                      description: 'Earn ₹100 when your first friend signs up',
                      rewardAmount: 100,
                      icon: 'gift',
                      color: 'rose',
                      isActive: true,
                      displayOrder: 1
                    })}
                  >
                    <Gift className="h-3 w-3 mr-1" />
                    First Referral
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setRewardForm({
                      title: '5 Referrals Milestone',
                      description: 'Bonus ₹250 for 5 successful referrals',
                      rewardAmount: 250,
                      icon: 'star',
                      color: 'blue',
                      isActive: true,
                      displayOrder: 2
                    })}
                  >
                    <Star className="h-3 w-3 mr-1" />
                    5 Referrals
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setRewardForm({
                      title: '10 Referrals Champion',
                      description: 'Earn ₹500 for reaching 10 referrals',
                      rewardAmount: 500,
                      icon: 'award',
                      color: 'green',
                      isActive: true,
                      displayOrder: 3
                    })}
                  >
                    <Award className="h-3 w-3 mr-1" />
                    10 Referrals
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setRewardForm({
                      title: 'Monthly Top Referrer',
                      description: 'Extra ₹1000 for monthly champion',
                      rewardAmount: 1000,
                      icon: 'trending-up',
                      color: 'yellow',
                      isActive: true,
                      displayOrder: 4
                    })}
                  >
                    <TrendingUp className="h-3 w-3 mr-1" />
                    Top Referrer
                  </Button>
                </div>
              </div>
            )}
            <div>
              <Label>Title</Label>
              <Input
                value={rewardForm.title}
                onChange={(e) => setRewardForm({ ...rewardForm, title: e.target.value })}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={rewardForm.description}
                onChange={(e) => setRewardForm({ ...rewardForm, description: e.target.value })}
              />
            </div>
            <div>
              <Label>Reward Amount (₹)</Label>
              <Input
                type="number"
                value={rewardForm.rewardAmount}
                onChange={(e) => setRewardForm({ ...rewardForm, rewardAmount: parseFloat(e.target.value) })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Icon</Label>
                <Select value={rewardForm.icon} onValueChange={(value) => setRewardForm({ ...rewardForm, icon: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gift">Gift</SelectItem>
                    <SelectItem value="star">Star</SelectItem>
                    <SelectItem value="award">Award</SelectItem>
                    <SelectItem value="wallet">Wallet</SelectItem>
                    <SelectItem value="dollar-sign">Dollar Sign</SelectItem>
                    <SelectItem value="trending-up">Trending Up</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Color</Label>
                <Select value={rewardForm.color} onValueChange={(value) => setRewardForm({ ...rewardForm, color: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rose">Rose</SelectItem>
                    <SelectItem value="blue">Blue</SelectItem>
                    <SelectItem value="green">Green</SelectItem>
                    <SelectItem value="yellow">Yellow</SelectItem>
                    <SelectItem value="purple">Purple</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={rewardForm.displayOrder}
                  onChange={(e) => setRewardForm({ ...rewardForm, displayOrder: parseInt(e.target.value) })}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  checked={rewardForm.isActive}
                  onChange={(e) => setRewardForm({ ...rewardForm, isActive: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label>Active</Label>
              </div>
            </div>

            <div className="border-t pt-4">
              <Label className="text-base mb-3 block">Qualification Criteria</Label>
              
              <div className="space-y-4">
                <div>
                  <Label>Criteria Type</Label>
                  <Select value={rewardForm.criteriaType} onValueChange={(value: any) => setRewardForm({ ...rewardForm, criteriaType: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="referral_count">Referral Count</SelectItem>
                      <SelectItem value="plan_sales">Plan Sales</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {rewardForm.criteriaType === 'referral_count' && (
                  <div>
                    <Label>Minimum Referrals Required</Label>
                    <Input
                      type="number"
                      value={rewardForm.minReferrals}
                      onChange={(e) => setRewardForm({ ...rewardForm, minReferrals: parseInt(e.target.value) || 0 })}
                      placeholder="e.g., 5"
                    />
                  </div>
                )}

                {rewardForm.criteriaType === 'plan_sales' && (
                  <div>
                    <Label>Minimum Plans Sold Required</Label>
                    <Input
                      type="number"
                      value={rewardForm.minPlansSold}
                      onChange={(e) => setRewardForm({ ...rewardForm, minPlansSold: parseInt(e.target.value) || 0 })}
                      placeholder="e.g., 10"
                    />
                  </div>
                )}

                <div>
                  <Label>Timeframe</Label>
                  <Select value={rewardForm.timeframe} onValueChange={(value: any) => setRewardForm({ ...rewardForm, timeframe: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lifetime">Lifetime</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Monthly rewards reset each month
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={rewardForm.autoCalculate}
                    onChange={(e) => setRewardForm({ ...rewardForm, autoCalculate: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <div>
                    <Label>Auto-Calculate Qualified Users</Label>
                    <p className="text-xs text-gray-500">
                      Automatically find users who meet criteria
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={rewardForm.requiresApproval}
                    onChange={(e) => setRewardForm({ ...rewardForm, requiresApproval: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <div>
                    <Label>Requires Admin Approval</Label>
                    <p className="text-xs text-gray-500">
                      Admin must manually grant rewards
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRewardDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveReward}>
              Save Reward
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Referral Settings</DialogTitle>
            <DialogDescription>Configure referral program settings</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Referral Type</Label>
              <Select 
                value={settingsForm.referralType} 
                onValueChange={(value) => setSettingsForm({ ...settingsForm, referralType: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed Amount</SelectItem>
                  <SelectItem value="percentage">Percentage</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {settingsForm.referralType === 'fixed' ? (
              <div>
                <Label>Referral Amount (₹)</Label>
                <Input
                  type="number"
                  value={settingsForm.referralAmount}
                  onChange={(e) => setSettingsForm({ ...settingsForm, referralAmount: parseFloat(e.target.value) })}
                />
              </div>
            ) : (
              <div>
                <Label>Referral Percentage (%)</Label>
                <Input
                  type="number"
                  value={settingsForm.referralPercentage}
                  onChange={(e) => setSettingsForm({ ...settingsForm, referralPercentage: parseFloat(e.target.value) })}
                />
              </div>
            )}

            <div>
              <Label>Minimum Payout Amount (₹)</Label>
              <Input
                type="number"
                value={settingsForm.minimumPayoutAmount}
                onChange={(e) => setSettingsForm({ ...settingsForm, minimumPayoutAmount: parseFloat(e.target.value) })}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settingsForm.autoApproveReferrals}
                  onChange={(e) => setSettingsForm({ ...settingsForm, autoApproveReferrals: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label>Auto-approve referral earnings</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settingsForm.requireOrderForReferral}
                  onChange={(e) => setSettingsForm({ ...settingsForm, requireOrderForReferral: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label>Require order/purchase for referral earning</Label>
              </div>
            </div>

            <div>
              <Label>Welcome Message</Label>
              <Textarea
                value={settingsForm.welcomeMessage}
                onChange={(e) => setSettingsForm({ ...settingsForm, welcomeMessage: e.target.value })}
                rows={2}
              />
            </div>

            <div>
              <Label>Terms & Conditions</Label>
              <Textarea
                value={settingsForm.termsAndConditions}
                onChange={(e) => setSettingsForm({ ...settingsForm, termsAndConditions: e.target.value })}
                rows={4}
              />
            </div>
            
            {/* Earning Structure Messages Section */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Label className="text-base font-semibold">Earning Structure Messages</Label>
                  <p className="text-sm text-gray-500 mt-1">
                    Add custom messages to explain your earning structure to users
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const updated = [...settingsForm.earningStructureMessages, {
                      title: 'New Message',
                      description: 'Description here',
                      amount: 100
                    }];
                    setSettingsForm({ ...settingsForm, earningStructureMessages: updated });
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Message
                </Button>
              </div>
              
              {settingsForm.earningStructureMessages.length > 0 ? (
                <div className="space-y-3">
                  {settingsForm.earningStructureMessages.map((msg, index) => (
                    <div key={index} className="p-4 border rounded-lg bg-gray-50">
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs">Title</Label>
                          <Input
                            className="h-9"
                            value={msg.title}
                            onChange={(e) => {
                              const updated = [...settingsForm.earningStructureMessages];
                              updated[index].title = e.target.value;
                              setSettingsForm({ ...settingsForm, earningStructureMessages: updated });
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Description</Label>
                          <Input
                            className="h-9"
                            value={msg.description}
                            onChange={(e) => {
                              const updated = [...settingsForm.earningStructureMessages];
                              updated[index].description = e.target.value;
                              setSettingsForm({ ...settingsForm, earningStructureMessages: updated });
                            }}
                          />
                        </div>
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <Label className="text-xs">Amount (₹)</Label>
                            <Input
                              type="number"
                              className="h-9"
                              value={msg.amount}
                              onChange={(e) => {
                                const updated = [...settingsForm.earningStructureMessages];
                                updated[index].amount = parseFloat(e.target.value) || 0;
                                setSettingsForm({ ...settingsForm, earningStructureMessages: updated });
                              }}
                            />
                          </div>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                              const updated = settingsForm.earningStructureMessages.filter((_, i) => i !== index);
                              setSettingsForm({ ...settingsForm, earningStructureMessages: updated });
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-sm text-gray-500 bg-gray-50 rounded border-dashed border-2">
                  No custom earning messages. Click "Add Message" to create one.
                </div>
              )}
            </div>
            
            {/* Plan-Wise Commissions Section */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Label className="text-base font-semibold">Plan-Wise Commissions</Label>
                  <p className="text-sm text-gray-500 mt-1">
                    Set specific commission for each plan. Changes are instant and won't affect old commissions.
                  </p>
                </div>
              </div>
              
              {plans.length > 0 ? (
                <div className="space-y-3">
                  {plans.map((plan: any) => {
                    const planCommission = settingsForm.planCommissions.find(pc => pc.planId === plan._id) || {
                      planId: plan._id,
                      planName: plan.name,
                      planPrice: plan.finalPrice || plan.price,
                      commissionType: 'fixed',
                      commissionValue: 100
                    };
                    
                    const maxCommission = planCommission.commissionType === 'percentage' ? 100 : (plan.finalPrice || plan.price);
                    const isInvalid = planCommission.commissionValue > maxCommission;
                    
                    return (
                      <div key={plan._id} className="p-4 border rounded-lg bg-gray-50">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-semibold text-sm">{plan.name}</h4>
                            <p className="text-xs text-gray-600">Price: ₹{plan.finalPrice || plan.price}</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Commission Type</Label>
                            <Select
                              value={planCommission.commissionType}
                              onValueChange={(value: 'fixed' | 'percentage') => {
                                const updated = settingsForm.planCommissions.filter(pc => pc.planId !== plan._id);
                                updated.push({
                                  ...planCommission,
                                  commissionType: value,
                                  commissionValue: value === 'percentage' ? 10 : 100
                                });
                                setSettingsForm({ ...settingsForm, planCommissions: updated });
                              }}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="fixed">Fixed (₹)</SelectItem>
                                <SelectItem value="percentage">Percentage (%)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label className="text-xs">
                              {planCommission.commissionType === 'fixed' ? 'Amount (₹)' : 'Percentage (%)'}
                            </Label>
                            <div className="relative">
                              <Input
                                type="number"
                                className={`h-9 ${isInvalid ? 'border-red-500' : ''}`}
                                value={planCommission.commissionValue}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value) || 0;
                                  const updated = settingsForm.planCommissions.filter(pc => pc.planId !== plan._id);
                                  updated.push({
                                    ...planCommission,
                                    commissionValue: value
                                  });
                                  setSettingsForm({ ...settingsForm, planCommissions: updated });
                                }}
                                max={maxCommission}
                              />
                              {isInvalid && (
                                <p className="text-xs text-red-600 mt-1">
                                  Cannot exceed {planCommission.commissionType === 'percentage' ? '100%' : `₹${plan.finalPrice || plan.price}`}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                          Commission will be: ₹
                          {planCommission.commissionType === 'percentage'
                            ? Math.min(((plan.finalPrice || plan.price) * planCommission.commissionValue) / 100, plan.finalPrice || plan.price).toFixed(2)
                            : Math.min(planCommission.commissionValue, plan.finalPrice || plan.price).toFixed(2)
                          } per referral
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6 text-sm text-gray-500 bg-gray-50 rounded border-dashed border-2">
                  No plans available. Create plans first in Admin → Plans section.
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSettings}
              disabled={settingsForm.planCommissions.some((pc: any) => {
                const plan = plans.find(p => p._id === pc.planId);
                if (!plan) return false;
                const maxValue = pc.commissionType === 'percentage' ? 100 : (plan.finalPrice || plan.price);
                return pc.commissionValue > maxValue;
              })}
            >
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}